import { SignalingService, TurnCredentials } from './signaling.service';
import { WebRTCService } from './webrtc.service';
import { handlePacketMonitorMessage } from './cloud-packet-monitor';

export type CameraState =
  | 'pending'              // page loaded, camera queued
  | 'signaling'            // WebRTC signaling exchange happening
  | 'waiting_stream'       // signaling done, waiting for RTP packets
  | 'rtc_established'      // stream is playing
  | 'stalled'              // was playing, frames stopped
  | 'reconnecting'         // resubscribe in progress
  | 'node_unreachable'     // local node down or tunnel broken
  | 'signaling_error'      // signaling path failed
  | 'camera_unreachable'   // pipeline dead / camera not streaming
  | 'network_error'        // cloud FE ↔ cloud BE socket / ICE failed
  | 'slow_network'         // too many packet/frame drops
  | 'access_denied';       // acting user has no RBAC access to this camera (TERMINAL — never retried)

export interface CameraInfo {
  cameraId: string;
  cameraName: string;
  state: CameraState;
  stream: MediaStream | null;
  retryCount: number;
  stallRetryCount: number;
  trackReceivedAt: number;
  /**
   * The MediaStream ontrack last handed us, KEPT even while the tile is showing
   * an error. `ontrack` fires only on the transition INTO receiving, so once we
   * drop our reference to a stream the browser has no event that hands it back:
   * media can be flowing on that transceiver again (camera back online, pipeline
   * restarted) with the tile still black. Recovery re-adopts this stream when the
   * track unmutes — see tryReadopt().
   */
  lastStream: MediaStream | null;
  /**
   * Per-camera recovery attempts that produced NO track. Reset whenever media
   * flows. Drives escalation to a full node rebuild.
   */
  recoveryAttempts: number;
  /**
   * Consecutive re-adoptions of `lastStream` not yet confirmed by real playback.
   * A track can be receiving RTP that the decoder cannot use (corrupt stream, no
   * keyframe yet), and re-adopting that forever would stop the camera from ever
   * reaching the resubscribe that asks the ULLFU for a fresh keyframe — so
   * re-adoption gives up after a couple of unconfirmed tries.
   */
  readoptCount: number;
}

export interface NodeConnection {
  nodeId: string;
  nodeName: string;
  signaling: SignalingService | null;
  webrtc: WebRTCService | null;
  turnCredentials: TurnCredentials | null;
  cameras: Map<string, CameraInfo>;
  socketState: 'closed' | 'connecting' | 'open';
  rtcState: 'new' | 'connecting' | 'connected' | 'failed' | 'disconnected';
  /** Monotonically increasing — invalidates all stale callbacks from prior connections */
  version: number;
  /**
   * Page-level keep-warm flag. When true the WS+PC are opened eagerly (with zero
   * cameras) and kept alive even when no cameras are subscribed — so clicking a
   * hexagon only adds a track instead of rebuilding the whole connection.
   */
  keptAlive: boolean;
}

type OnStreamCallback = (nodeId: string, cameraId: string, stream: MediaStream | null) => void;
type OnCameraStateCallback = (nodeId: string, cameraId: string, state: CameraState) => void;
type OnNodeErrorCallback = (nodeId: string, error: string) => void;

const FAST_RETRY_COUNT = 8;           // First N retries use fast backoff
const FAST_BACKOFF_BASE_MS = 5000;    // 5s base for fast retries
const FAST_BACKOFF_MAX_MS = 30000;    // 30s max for fast retries  
const SLOW_RETRY_INTERVAL_MS = 30000; // After fast retries exhausted: retry every 30s forever
// Client-side guard on concurrently-subscribed camera tracks. Higher than a
// single 8x8 grid (64) because ONE shared manager now serves every page — during
// a navigation the previous page's tracks linger briefly while the next page
// subscribes, so the two sets can transiently overlap. The real ceiling is
// server-side (ULLFU MAX_VIEWERS / UDP port range), this is just a sanity cap.
const MAX_STREAMS = 128;
const SIGNALING_TIMEOUT_MS = 25000;
const WAITING_STREAM_TIMEOUT_MS = 20000; // per-camera: if no track after 20s in waiting_stream → camera_unreachable
// How long to keep playing streams alive after a tunnel_disconnected before giving up
// and doing a full reconnect. The tunnel only carries SIGNALING — WebRTC media flows
// browser↔TURN↔ULLFU and survives a brief tunnel blip — so a transient flap shouldn't
// blank the previews. Cancelled if a tunnel_reconnected arrives first.
const TUNNEL_RECOVER_GRACE_MS = 12000;
// Gap between the unsubscribe and the subscribe of a forced resubscribe. The ULLFU
// batches renegotiations, so the two must not land in the same batch window.
const RESUBSCRIBE_GAP_MS = 500;
// A camera whose resubscribes produce NO answer at all from the ULLFU (no offer, no
// camera-status) this many times is escalated to a full node rebuild. Silence is
// the signature of a state desync — a camera the ULLFU believes is already
// subscribed, or a PeerConnection that can no longer negotiate — and no amount of
// resubscribing on that session will ever fix it.
const CAMERA_ESCALATE_AFTER = 3;
// A rebuild briefly interrupts every OTHER camera on the node, so rate-limit it.
const NODE_REBUILD_COOLDOWN_MS = 60000;
const DEBUG = false; // Set true to enable verbose logging

/**
 * NodeConnectionManager — manages streaming from multiple local nodes.
 *
 * Architecture:
 *   ONE WebSocket + ONE RTCPeerConnection PER NODE (not per camera).
 *   Multiple cameras subscribed over that single connection.
 *   ULLFU sets stream IDs as "camera-{cameraId}" for demuxing tracks.
 *
 * Reconnect strategy (v2 — version-gated, single path):
 *   - ALL reconnects go through scheduleReconnect() → doReconnect()
 *   - Each node has a monotonic `version` counter
 *   - Every async callback (WS onclose, RTC state change, timers) checks
 *     `node.version === myVersion` and bails out if stale
 *   - bumpAndTeardown() increments version + cancels all timers + tears down
 *   - This makes it IMPOSSIBLE for a stale callback to tear down a new connection
 */
export class NodeConnectionManager {
  private nodes: Map<string, NodeConnection> = new Map();
  private onStream: OnStreamCallback;
  private onCameraState: OnCameraStateCallback;
  private onNodeError: OnNodeErrorCallback;
  private totalActiveStreams = 0;
  private destroyed = false;

  // Timers (keyed by nodeId)
  private reconnectTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private signalingTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private disconnectTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  /** Per-node tunnel-recovery grace timers — fire a full reconnect only if a dropped
   *  tunnel hasn't reconnected within TUNNEL_RECOVER_GRACE_MS. */
  private tunnelRecoverTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  /** Per-camera stall resubscribe timers (keyed by "nodeId:cameraId") */
  private stallTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  /** Per-camera waiting_stream timeout timers (keyed by "nodeId:cameraId") */
  private waitingStreamTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private livenessInterval: ReturnType<typeof setInterval> | null = null;
  /** Last full-rebuild time per node — rate-limits escalateToNodeRebuild(). */
  private lastNodeRebuild: Map<string, number> = new Map();

  // Event handlers
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  constructor(
    onStream: OnStreamCallback,
    onCameraState: OnCameraStateCallback,
    onNodeError: OnNodeErrorCallback
  ) {
    this.onStream = onStream;
    this.onCameraState = onCameraState;
    this.onNodeError = onNodeError;

    // Visibility: check stale connections when tab refocused
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && !this.destroyed) {
        this.checkAllNodes();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Online: schedule reconnect for DOWN nodes only (healthy ones untouched)
    this.onlineHandler = () => {
      if (this.destroyed) return;
      console.log('[NodeConnMgr] Network online — will reconnect unhealthy nodes');
      // Small delay to let the network stabilize
      setTimeout(() => {
        if (this.destroyed) return;
        for (const [, node] of this.nodes) {
          if (node.cameras.size === 0 && !node.keptAlive) continue;
          if (!this.isNodeHealthy(node)) {
            // Reset retry counts so we get a fresh set of attempts
            for (const [, cam] of node.cameras) {
              cam.retryCount = 0;
              cam.stallRetryCount = 0;
            }
            console.log(`[NodeConnMgr] Online: scheduling reconnect for node=${node.nodeId}`);
            this.scheduleReconnect(node, 1000);
          }
        }
      }, 2000);
    };
    window.addEventListener('online', this.onlineHandler);

    // Offline: teardown all, mark offline (online handler will reconnect later)
    this.offlineHandler = () => {
      if (this.destroyed) return;
      console.log('[NodeConnMgr] Network offline — tearing down all');
      for (const [, node] of this.nodes) {
        if (node.cameras.size === 0 && !node.keptAlive) continue;
        this.bumpAndTeardown(node);
        for (const [, cam] of node.cameras) {
          cam.state = 'network_error';
          cam.retryCount = 0;
          cam.stallRetryCount = 0;
          this.onCameraState(node.nodeId, cam.cameraId, 'network_error');
        }
      }
    };
    window.addEventListener('offline', this.offlineHandler);

    // Periodic liveness — check for dead connections and silently-recovered
    // cameras. 10s (not 30s) because this also drives stream re-adoption, which
    // is user-visible: it is how long a tile can stay black after its media has
    // actually come back.
    this.livenessInterval = setInterval(() => {
      if (!this.destroyed) this.checkAllNodes();
    }, 10000);
  }

  // ─── Health check ─────────────────────────────────────────────

  private isNodeHealthy(node: NodeConnection): boolean {
    if (node.socketState === 'connecting') return true; // still connecting, don't interfere
    const pc = node.webrtc?.getPeerConnection?.();
    const pcState = pc?.connectionState;
    const pcOk = pcState === 'connected' || pcState === 'connecting' || pcState === 'new';
    const wsOk = node.signaling?.getWs?.()?.readyState === WebSocket.OPEN;
    return !!(pcOk && wsOk);
  }

  /** Check all nodes, schedule reconnect for unhealthy ones */
  private checkAllNodes(): void {
    for (const [, node] of this.nodes) {
      if (node.cameras.size === 0 && !node.keptAlive) continue;
      // Safety net for the unmute listener: on a healthy connection, any camera
      // we are not rendering whose track is receiving again gets re-adopted. This
      // is what makes a stream come back on its own after the camera/pipeline
      // recovers, with no signaling and no page reload.
      for (const [, cam] of node.cameras) {
        if (!cam.stream) this.tryReadopt(node, cam);
      }
      if (this.isNodeHealthy(node)) continue;
      if (this.reconnectTimers.has(node.nodeId)) continue; // already scheduled
      // Check if all cameras are in a terminal state with no pending reconnect
      const retryNum = node.cameras.values().next().value?.retryCount ?? 0;
      const backoff = this.getBackoffMs(retryNum);
      if (DEBUG) console.log(`[NodeConnMgr] Liveness: node ${node.nodeId} unhealthy (socket=${node.socketState} rtc=${node.rtcState}) — scheduling reconnect in ${backoff}ms`);
      this.scheduleReconnect(node, backoff);
    }
  }

  // ─── Core reconnect: single path, version-gated ──────────────

  /** Bump node version, cancel all timers, teardown connection */
  private bumpAndTeardown(node: NodeConnection): void {
    node.version++;
    if (DEBUG) console.log(`[NodeConnMgr] bumpAndTeardown node=${node.nodeId} → v=${node.version}`);
    // Cancel all pending timers for this node
    const rt = this.reconnectTimers.get(node.nodeId);
    if (rt) { clearTimeout(rt); this.reconnectTimers.delete(node.nodeId); }
    const st = this.signalingTimers.get(node.nodeId);
    if (st) { clearTimeout(st); this.signalingTimers.delete(node.nodeId); }
    const dt = this.disconnectTimers.get(node.nodeId);
    if (dt) { clearTimeout(dt); this.disconnectTimers.delete(node.nodeId); }
    const trt = this.tunnelRecoverTimers.get(node.nodeId);
    if (trt) { clearTimeout(trt); this.tunnelRecoverTimers.delete(node.nodeId); }
    // Clear per-camera stall timers for this node
    for (const [key, timer] of this.stallTimers) {
      if (key.startsWith(node.nodeId + ':')) {
        clearTimeout(timer);
        this.stallTimers.delete(key);
      }
    }
    // Clear per-camera waiting_stream timers for this node
    for (const [key, timer] of this.waitingStreamTimers) {
      if (key.startsWith(node.nodeId + ':')) {
        clearTimeout(timer);
        this.waitingStreamTimers.delete(key);
      }
    }
    // Teardown
    node.webrtc?.close();
    node.webrtc = null;
    node.signaling?.close();
    node.signaling = null;
    node.turnCredentials = null;
    node.socketState = 'closed';
    node.rtcState = 'new';
  }

  /**
   * THE ONLY entry point for scheduling a reconnect.
   * - Cancels any existing pending reconnect timer for this node
   * - Checks health before actually reconnecting
   * - bumpAndTeardown inside doReconnect invalidates stale callbacks
   */
  private scheduleReconnect(node: NodeConnection, delayMs: number): void {
    if (this.destroyed || (node.cameras.size === 0 && !node.keptAlive)) return;

    // Cancel existing timer — only ONE pending reconnect per node
    const existing = this.reconnectTimers.get(node.nodeId);
    if (existing) clearTimeout(existing);

    if (DEBUG) console.log(`[NodeConnMgr] scheduleReconnect node=${node.nodeId} in ${delayMs}ms`);

    const timer = setTimeout(() => {
      this.reconnectTimers.delete(node.nodeId);
      if (this.destroyed || (node.cameras.size === 0 && !node.keptAlive)) return;

      // Don't reconnect if already healthy
      if (this.isNodeHealthy(node)) {
        if (DEBUG) console.log(`[NodeConnMgr] Skip reconnect — node ${node.nodeId} is healthy`);
        return;
      }

      this.doReconnect(node).catch(e => console.error('[NodeConnMgr] doReconnect error:', e));
    }, delayMs);
    this.reconnectTimers.set(node.nodeId, timer);
  }

  /** The actual reconnect logic — called only from scheduleReconnect */
  private async doReconnect(node: NodeConnection): Promise<void> {
    // Bump version = invalidate ALL stale callbacks from old connection
    this.bumpAndTeardown(node);
    const myVersion = node.version;

    // Increment retry count (never resets to 0 here — only on success or online event)
    for (const [, cam] of node.cameras) {
      cam.retryCount++;
    }

    const firstCam = node.cameras.values().next().value;
    const retryNum = firstCam?.retryCount ?? 1;
    if (DEBUG) console.log(`[NodeConnMgr] doReconnect node=${node.nodeId} v=${myVersion} retry=${retryNum}`);

    // Set pending state. The retained stream references belong to the PC we just
    // tore down, so drop them here — nothing can be re-adopted across a rebuild.
    for (const [, cam] of node.cameras) {
      cam.state = 'pending';
      cam.stream = null;
      cam.lastStream = null;
      cam.recoveryAttempts = 0;
      cam.readoptCount = 0;
      cam.trackReceivedAt = 0; // clear any retry-in-progress sentinel
      this.onCameraState(node.nodeId, cam.cameraId, 'pending');
    }

    // If browser is offline, don't waste a try — the online handler will schedule reconnect
    if (!navigator.onLine) {
      if (DEBUG) console.log(`[NodeConnMgr] Browser offline — waiting for online event before reconnecting node=${node.nodeId}`);
      for (const [, cam] of node.cameras) {
        cam.state = 'network_error';
        cam.retryCount = Math.max(0, cam.retryCount - 1); // Don't count this
        this.onCameraState(node.nodeId, cam.cameraId, 'network_error');
      }
      return;
    }

    try {
      await this.connectNode(node, myVersion);
    } catch (err) {
      console.warn(`[NodeConnMgr] connectNode failed for ${node.nodeId}:`, err);
      if (node.version === myVersion && !this.destroyed) {
        // Calculate backoff: fast for first N, then slow forever
        const backoff = this.getBackoffMs(retryNum);
        if (DEBUG) console.log(`[NodeConnMgr] Will retry node=${node.nodeId} in ${backoff}ms (attempt ${retryNum})`);
        for (const [, cam] of node.cameras) {
          cam.state = 'node_unreachable';
          this.onCameraState(node.nodeId, cam.cameraId, 'node_unreachable');
        }
        this.scheduleReconnect(node, backoff);
      }
    }
  }

  /** Tiered backoff: fast exponential for first N, then fixed slow interval forever */
  private getBackoffMs(retryNum: number): number {
    if (retryNum <= FAST_RETRY_COUNT) {
      return Math.min(FAST_BACKOFF_BASE_MS * 2 ** (retryNum - 1), FAST_BACKOFF_MAX_MS);
    }
    return SLOW_RETRY_INTERVAL_MS;
  }

  // ─── Public API ───────────────────────────────────────────────

  getTotalActiveStreams(): number {
    return this.totalActiveStreams;
  }

  /**
   * Open and keep ONE WS+PC warm for a node, eagerly, with zero cameras.
   * Called once when the hexagon page mounts so the first camera click only
   * adds a track rather than building a fresh connection. Idempotent.
   */
  async ensureConnected(nodeId: string, nodeName: string): Promise<void> {
    if (this.destroyed) return;

    let node = this.nodes.get(nodeId);
    if (!node) {
      node = {
        nodeId, nodeName,
        signaling: null, webrtc: null, turnCredentials: null,
        cameras: new Map(),
        socketState: 'closed', rtcState: 'new',
        version: 0,
        keptAlive: true,
      };
      this.nodes.set(nodeId, node);
    } else {
      node.keptAlive = true;
    }

    if (node.socketState === 'closed') {
      const myVersion = node.version;
      try {
        await this.connectNode(node, myVersion);
      } catch (err) {
        console.warn(`[NodeConnMgr] ensureConnected failed for ${nodeId}:`, err);
        this.scheduleReconnect(node, 3000);
      }
    }
  }

  /**
   * Release the page-level keep-warm hold. If no cameras remain, tear the
   * connection down (so it doesn't linger after the page unmounts).
   */
  releaseKeepAlive(nodeId: string): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;
    node.keptAlive = false;
    if (node.cameras.size === 0) {
      this.bumpAndTeardown(node);
      this.nodes.delete(nodeId);
    }
  }

  async addCamera(
    nodeId: string,
    nodeName: string,
    cameraId: string,
    cameraName: string,
    _quality: 'low' | 'high' = 'low'
  ): Promise<boolean> {
    if (this.totalActiveStreams >= MAX_STREAMS) return false;

    let node = this.nodes.get(nodeId);
    if (!node) {
      node = {
        nodeId, nodeName,
        signaling: null, webrtc: null, turnCredentials: null,
        cameras: new Map(),
        socketState: 'closed', rtcState: 'new',
        version: 0,
        keptAlive: false,
      };
      this.nodes.set(nodeId, node);
    }

    if (node.cameras.has(cameraId)) return true;

    const cam: CameraInfo = {
      cameraId, cameraName, state: 'pending', stream: null, retryCount: 0, stallRetryCount: 0, trackReceivedAt: 0,
      lastStream: null, recoveryAttempts: 0, readoptCount: 0,
    };
    node.cameras.set(cameraId, cam);
    this.totalActiveStreams++;
    this.onCameraState(nodeId, cameraId, 'pending');

    if (node.socketState === 'closed') {
      const myVersion = node.version;
      try {
        await this.connectNode(node, myVersion);
      } catch (err) {
        console.warn(`[NodeConnMgr] Initial connectNode failed for ${nodeId}:`, err);
        this.scheduleReconnect(node, 3000);
      }
    } else if (node.signaling && node.socketState === 'open') {
      node.signaling.subscribe([cameraId]);
      cam.state = 'signaling';
      this.onCameraState(nodeId, cameraId, 'signaling');
      // The connection may have been kept warm with no cameras (no signaling
      // timer running) — (re)arm it so a subscribe that never yields a track
      // still triggers a reconnect.
      this.armSignalingTimeout(node);
      // Per-camera watchdog as well: the node-level one self-cancels as soon as
      // ANY camera is playing, so on a page that already has working tiles it
      // would never fire for this one. That is how a freshly-clicked camera
      // could sit on "signaling..." indefinitely while its neighbours played.
      this.startWaitingStreamTimeout(node, cam);
    }

    return true;
  }

  removeCamera(nodeId: string, cameraId: string): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;
    const cam = node.cameras.get(cameraId);
    if (!cam) return;

    if (node.signaling && node.socketState === 'open') {
      node.signaling.unsubscribe([cameraId]);
    }

    cam.stream = null;
    node.cameras.delete(cameraId);
    this.totalActiveStreams--;
    this.onStream(nodeId, cameraId, null);

    if (node.cameras.size === 0 && !node.keptAlive) {
      this.bumpAndTeardown(node);
      this.nodes.delete(nodeId);
    }
  }

  /**
   * Retry a single camera's stream.
   * Called by the player's auto-retry countdown or manual "retry now" button.
   *
   * If the node connection (WS+RTC) is healthy, does a per-camera unsubscribe/resubscribe.
   * If the node connection is down, triggers a full node-level reconnect.
   *
   * Debounced: ignores calls within 3s of the last retry for the same camera
   * to prevent rapid-fire retries from competing timer sources.
   */
  async retryCamera(nodeId: string, cameraId: string): Promise<void> {
    const node = this.nodes.get(nodeId);
    if (!node) return;
    const cam = node.cameras.get(cameraId);
    if (!cam) return;
    if (cam.state === 'access_denied') return; // terminal — RBAC denial is not retryable

    const stallKey = `${nodeId}:${cameraId}`;
    if (cam.trackReceivedAt < 0) return; // sentinel: retry in progress

    // Cancel any existing stall resubscribe chain
    const stallT = this.stallTimers.get(stallKey);
    if (stallT) { clearTimeout(stallT); this.stallTimers.delete(stallKey); }
    this.clearWaitingStreamTimeout(nodeId, cameraId);

    // Cheapest fix first: the track we already hold may be receiving again.
    if (this.tryReadopt(node, cam)) return;

    // Enough failed resubscribes means the fault is in the session, not the
    // camera — rebuild the whole connection instead of looping forever.
    if (cam.recoveryAttempts >= CAMERA_ESCALATE_AFTER &&
        this.escalateToNodeRebuild(node, `camera ${cameraId} retry limit reached`)) {
      return;
    }

    // Don't reset retryCount — preserve backoff state.
    // retryCount is only reset on successful track receipt (in onTrack handler).
    cam.stallRetryCount = 0;
    cam.state = 'pending';
    cam.stream = null;
    cam.trackReceivedAt = -1; // sentinel: retry in progress (debounce)
    this.onStream(nodeId, cameraId, null);
    this.onCameraState(nodeId, cameraId, 'pending');

    // Anything other than an open socket on a live PC needs the node rebuilt.
    // 'disconnected' used to fall through to the resubscribe branch below, where
    // the camera was parked in 'signaling' with no watchdog — a loader state the
    // player never retries out of, so that camera was gone until a page reload.
    const transportUsable = node.socketState === 'open' && !!node.signaling &&
      (node.rtcState === 'connected' || node.rtcState === 'connecting' || node.rtcState === 'new');

    if (!transportUsable) {
      // Node connection is down — need full reconnect
      // Use backoff based on retry count to prevent hammering
      const backoff = Math.min(3000, this.getBackoffMs(cam.retryCount));
      cam.retryCount++;
      cam.trackReceivedAt = 0; // clear the debounce sentinel — the reconnect owns recovery now
      this.scheduleReconnect(node, backoff);
      return;
    }

    cam.retryCount++;
    // ALWAYS clear the in-progress sentinel — otherwise trackReceivedAt stays -1
    // forever and every future retryCamera() for this camera no-ops, permanently
    // disabling the player's retry button/countdown.
    cam.trackReceivedAt = 0;
    this.resubscribeFresh(node, [cam]);
  }

  changeQuality(nodeId: string, cameraId: string, quality: 'low' | 'high'): void {
    const node = this.nodes.get(nodeId);
    if (!node?.signaling) return;
    node.signaling.send({ type: 'quality-change', cameraId, quality });
  }

  /** Called by the player when a camera transitions to playing (frames confirmed).
   *  Clears stall recovery state so the resubscribe cycle stops. */
  handleCameraPlaying(nodeId: string, cameraId: string): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;
    const cam = node.cameras.get(cameraId);
    if (!cam) return;
    // Frames are decoding: whatever we last handed the player was the right
    // stream, so restore the re-adoption budget. Done before the early return
    // below because a camera recovered purely by re-adoption never entered stall
    // recovery and would otherwise keep a spent budget forever.
    cam.readoptCount = 0;
    cam.recoveryAttempts = 0;
    if (cam.stallRetryCount === 0) return; // not in stall recovery
    if (DEBUG) console.log(`[NodeConnMgr] Camera ${cameraId} confirmed playing — clearing stall recovery`);
    cam.stallRetryCount = 0;
    cam.retryCount = 0;
    cam.state = 'rtc_established';
    // Cancel pending stall timer
    const stallKey = `${nodeId}:${cameraId}`;
    const stallT = this.stallTimers.get(stallKey);
    if (stallT) { clearTimeout(stallT); this.stallTimers.delete(stallKey); }
    this.onCameraState(nodeId, cameraId, 'rtc_established');
  }

  handleCameraStall(nodeId: string, cameraId: string): void {
    const node = this.nodes.get(nodeId);
    if (!node) return;
    const cam = node.cameras.get(cameraId);
    if (!cam) return;

    // If node connection is already down, node-level reconnect handles it
    if (!this.isNodeHealthy(node)) return;

    // Skip if camera is already in a non-streaming / error state
    if (cam.state === 'pending' || cam.state === 'signaling' || cam.state === 'waiting_stream' ||
        cam.state === 'node_unreachable' || cam.state === 'signaling_error' ||
        cam.state === 'camera_unreachable' || cam.state === 'network_error' ||
        cam.state === 'reconnecting') return;

    // Grace period after track receipt — don't stall-detect too early
    if (cam.trackReceivedAt > 0 && Date.now() - cam.trackReceivedAt < 15000) return;

    // Mark as camera_unreachable — the LiveStreamPlayer countdown will
    // call retryCamera() with proper progressive backoff (3→5→10→15s).
    console.warn(`[NodeConnMgr] Camera ${cameraId} stall detected — marking camera_unreachable`);
    cam.state = 'camera_unreachable';
    cam.stream = null;
    this.onStream(nodeId, cameraId, null);
    this.onCameraState(nodeId, cameraId, 'camera_unreachable');
  }

  /**
   * Re-adopt the stream we already hold for a camera when its track is live and
   * unmuted — the ULLFU IS sending media on the existing transceiver and the only
   * thing missing is our reference to it.
   *
   * This is the cheap half of recovery, and the half that used to be impossible.
   * `ontrack` fires only when a transceiver transitions into receiving, so after
   * handleCameraStall()/retryCamera() cleared `cam.stream` there was no browser
   * event left that could give it back: when the camera came back the media
   * flowed into a track nobody was rendering, and the tile stayed "camera
   * unreachable" until the page was reloaded. A muted track means no RTP is
   * arriving, so an unmuted one is exactly the signal we want.
   */
  private tryReadopt(node: NodeConnection, cam: CameraInfo): boolean {
    if (cam.state === 'access_denied') return false;
    const s = cam.lastStream;
    if (!s) return false;
    const track = s.getVideoTracks()[0];
    if (!track || track.readyState !== 'live' || track.muted) return false;
    // Unmuted means RTP is arriving, not that it decodes. Give up after a couple
    // of re-adoptions that never turned into playback so the camera falls through
    // to a resubscribe (which makes the ULLFU replay a GOP / request a keyframe).
    if (cam.readoptCount >= 2) {
      if (DEBUG) console.log(`[NodeConnMgr] Not re-adopting cam=${cam.cameraId} again — ${cam.readoptCount} unconfirmed re-adoptions`);
      return false;
    }
    cam.readoptCount++;
    console.log(`[NodeConnMgr] Camera ${cam.cameraId} media is flowing on the existing track — re-adopting stream (no resubscribe needed)`);
    cam.stream = s;
    cam.trackReceivedAt = Date.now();
    cam.state = 'rtc_established';
    cam.recoveryAttempts = 0;
    cam.retryCount = 0;
    cam.stallRetryCount = 0;
    this.clearWaitingStreamTimeout(node.nodeId, cam.cameraId);
    this.onStream(node.nodeId, cam.cameraId, s);
    this.onCameraState(node.nodeId, cam.cameraId, 'rtc_established');
    return true;
  }

  /**
   * Watch a received track so the camera recovers on its own. When the local
   * pipeline restarts or the camera comes back, the ULLFU resumes writing RTP into
   * the SAME track: the browser reports that as an `unmute` on the existing
   * track and NOT as a new `ontrack`. Without this listener nothing in the app
   * ever learns the picture is back.
   */
  private watchTrack(node: NodeConnection, cameraId: string, track: MediaStreamTrack, myVersion: number): void {
    track.onunmute = () => {
      if (this.destroyed || node.version !== myVersion) return;
      const cam = node.cameras.get(cameraId);
      if (!cam || cam.stream) return; // already rendering — nothing to recover
      this.tryReadopt(node, cam);
    };
  }

  /**
   * Force the ULLFU to re-offer a set of cameras: drop the tracks first, then
   * subscribe again after a gap.
   *
   * A bare `subscribe` for a camera the ULLFU still holds in its subscription map
   * is a silent no-op on that side — no offer, no track, no camera-status, no
   * error — so the tile sat in "waiting for stream" until the timeout and then
   * looped forever. Unsubscribing first guarantees the resubscribe produces a
   * fresh offer, and the gap keeps the remove and the add out of the same ULLFU
   * renegotiation batch.
   */
  private resubscribeFresh(node: NodeConnection, cams: CameraInfo[]): void {
    const targets = cams.filter((c) => c.state !== 'access_denied');
    if (!node.signaling || targets.length === 0) return;
    const ids = targets.map((c) => c.cameraId);
    node.signaling.unsubscribe(ids);
    for (const cam of targets) {
      cam.state = 'reconnecting';
      cam.stream = null;
      cam.trackReceivedAt = 0;
      this.onStream(node.nodeId, cam.cameraId, null);
      this.onCameraState(node.nodeId, cam.cameraId, 'reconnecting');
    }
    const v = node.version;
    setTimeout(() => {
      if (this.destroyed || node.version !== v || !node.signaling) return;
      const alive = ids.filter((id) => node.cameras.has(id));
      if (alive.length === 0) return;
      node.signaling.subscribe(alive);
      for (const id of alive) {
        const c = node.cameras.get(id);
        if (!c || c.state === 'access_denied') continue;
        c.state = node.rtcState === 'connected' ? 'waiting_stream' : 'signaling';
        this.onCameraState(node.nodeId, id, c.state);
        // Arm the watchdog in BOTH states. It used to be armed only for
        // waiting_stream, so a resubscribe issued while ICE was still settling
        // parked the camera in 'signaling' with no timer of any kind — the
        // player shows a loader there, not an error, so its retry countdown
        // never ran either and the camera never came back.
        this.startWaitingStreamTimeout(node, c);
      }
      this.armSignalingTimeout(node);
    }, RESUBSCRIBE_GAP_MS);
  }

  /**
   * Last rung of the recovery ladder: throw the node's WS + PeerConnection away
   * and build a new one.
   *
   * Per-camera resubscribing can only repair state that lives INSIDE a working
   * session. When the browser and the ULLFU disagree about what is subscribed, or
   * the PeerConnection can no longer apply the ULLFU's offers, every resubscribe is
   * a no-op and the camera is wedged for good — which is why a page reload was
   * the only known cure. A reload is precisely this: drop the session, build a
   * fresh one. Doing it here means the user never has to.
   */
  private escalateToNodeRebuild(node: NodeConnection, reason: string): boolean {
    const last = this.lastNodeRebuild.get(node.nodeId) ?? 0;
    if (Date.now() - last < NODE_REBUILD_COOLDOWN_MS) {
      if (DEBUG) console.log(`[NodeConnMgr] Rebuild for node=${node.nodeId} suppressed by cooldown (${reason})`);
      return false;
    }
    this.lastNodeRebuild.set(node.nodeId, Date.now());
    console.warn(`[NodeConnMgr] Rebuilding node=${node.nodeId} connection — ${reason}`);
    this.bumpAndTeardown(node);
    for (const [, cam] of node.cameras) {
      if (cam.state === 'access_denied') continue; // terminal — leave it alone
      cam.recoveryAttempts = 0;
      cam.readoptCount = 0;
      cam.retryCount = 0;
      cam.stallRetryCount = 0;
      cam.trackReceivedAt = 0;
      cam.state = 'reconnecting';
      this.onCameraState(node.nodeId, cam.cameraId, 'reconnecting');
    }
    this.scheduleReconnect(node, 500);
    return true;
  }

  /**
   * Start a per-camera timeout when entering waiting_stream.
   * If no track arrives within WAITING_STREAM_TIMEOUT_MS, mark camera_unreachable
   * and kick off the stall resubscribe loop so it keeps retrying.
   */
  private startWaitingStreamTimeout(node: NodeConnection, cam: CameraInfo): void {
    const key = `${node.nodeId}:${cam.cameraId}`;
    // Clear any existing timer
    const existing = this.waitingStreamTimers.get(key);
    if (existing) clearTimeout(existing);
    const v = node.version;
    const timer = setTimeout(() => {
      this.waitingStreamTimers.delete(key);
      if (this.destroyed || node.version !== v) return;
      const c = node.cameras.get(cam.cameraId);
      // 'signaling' is included because resubscribeFresh() arms this watchdog
      // there too — a camera resubscribed before ICE settled must not be able to
      // park in a loader state with no timer watching it.
      if (!c || (c.state !== 'waiting_stream' && c.state !== 'signaling')) return;
      // Media may already be flowing on the transceiver we hold: ontrack will
      // never fire again for it, so look before declaring the camera dead.
      if (this.tryReadopt(node, c)) return;
      c.recoveryAttempts++;
      console.warn(`[NodeConnMgr] Camera ${cam.cameraId} stuck in ${c.state} for ${WAITING_STREAM_TIMEOUT_MS / 1000}s (silent attempt ${c.recoveryAttempts}) — marking camera_unreachable`);
      c.state = 'camera_unreachable';
      this.onCameraState(node.nodeId, c.cameraId, 'camera_unreachable');
      // Reaching the timeout means the ULLFU said NOTHING about this camera — no
      // offer and no camera-status. A camera that is genuinely down gets a
      // 'camera-status offline' instead, which moves it out of waiting_stream and
      // makes this callback return above. So repeated silence is a state desync,
      // and only a new session can clear it.
      if (c.recoveryAttempts >= CAMERA_ESCALATE_AFTER) {
        this.escalateToNodeRebuild(
          node,
          `camera ${c.cameraId} got no response to ${c.recoveryAttempts} resubscribes`,
        );
        return;
      }
      // Player countdown will call retryCamera() to retry
    }, WAITING_STREAM_TIMEOUT_MS);
    this.waitingStreamTimers.set(key, timer);
  }

  /** Clear the waiting_stream timeout for a camera (e.g., when track arrives) */
  private clearWaitingStreamTimeout(nodeId: string, cameraId: string): void {
    const key = `${nodeId}:${cameraId}`;
    const t = this.waitingStreamTimers.get(key);
    if (t) { clearTimeout(t); this.waitingStreamTimers.delete(key); }
  }

  removeAllCameras(): void {
    for (const [, node] of this.nodes) {
      this.bumpAndTeardown(node);
    }
    this.nodes.clear();
    this.totalActiveStreams = 0;
  }

  /**
   * Get the RTCPeerConnection for a node (for stats/ping measurement).
   * Returns null if node not connected.
   */
  getNodePeerConnection(nodeId: string): RTCPeerConnection | null {
    const node = this.nodes.get(nodeId);
    return node?.webrtc?.getPeerConnection() || null;
  }

  /**
   * Debug/diagnostics: expose a node's live signaling WS + PC + sessionId so the
   * cameras-page debug overlay can read connection stats. Returns null if the
   * node isn't connected.
   */
  getNodeSignalingInfo(nodeId: string): { ws: WebSocket | null; pc: RTCPeerConnection | null; sessionId: string | null } | null {
    const node = this.nodes.get(nodeId);
    if (!node) return null;
    return {
      ws: node.signaling?.getWs() ?? null,
      pc: node.webrtc?.getPeerConnection() ?? null,
      sessionId: node.signaling?.getSessionId() ?? null,
    };
  }

  /**
   * Get all connected node IDs.
   */
  getConnectedNodeIds(): string[] {
    return Array.from(this.nodes.entries())
      .filter(([_, n]) => n.rtcState === 'connected')
      .map(([id]) => id);
  }

  /**
   * Graceful teardown for LOGOUT. Before ripping down transports, tell the
   * cloud/ULLFU to drop each session immediately (close-session) so it bypasses
   * the resume grace and frees the UDP port / egress now instead of 30s later.
   * A plain destroy() closes the WS silently — which the cloud treats as a
   * resumable blip — so logout must go through here.
   */
  shutdown(): void {
    for (const [, node] of this.nodes) {
      try {
        if (node.signaling && node.socketState === 'open') {
          const camIds = Array.from(node.cameras.keys());
          if (camIds.length > 0) node.signaling.unsubscribe(camIds);
          node.signaling.closeSession();
        }
      } catch { /* best-effort — never let teardown throw during logout */ }
    }
    this.destroy();
  }

  destroy(): void {
    this.destroyed = true;
    this.removeAllCameras();
    this.tunnelRecoverTimers.forEach((t) => clearTimeout(t));
    this.tunnelRecoverTimers.clear();
    this.lastNodeRebuild.clear();
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = null;
    }
    if (this.livenessInterval) {
      clearInterval(this.livenessInterval);
      this.livenessInterval = null;
    }
  }

  getCameraState(nodeId: string, cameraId: string): CameraState | undefined {
    return this.nodes.get(nodeId)?.cameras.get(cameraId)?.state;
  }

  // ─── Private: Node lifecycle ──────────────────────────────────

  /**
   * Connect to a node: open ONE WebSocket, init ONE WebRTC, subscribe ALL cameras.
   * ALL callbacks are gated on `node.version === myVersion`.
   */
  private async connectNode(node: NodeConnection, myVersion: number): Promise<void> {
    if (node.socketState !== 'closed') return;
    node.socketState = 'connecting';

    // 1. Signaling WS
    if (DEBUG) console.log(`[NodeConnMgr] connectNode: opening WS for node=${node.nodeId} v=${myVersion}`);
    const signaling = new SignalingService(node.nodeId);
    node.signaling = signaling;

    const { turnCredentials } = await signaling.connect(
      (msg) => {
        if (node.version !== myVersion) {
          if (DEBUG) console.log(`[NodeConnMgr] STALE msg (v=${myVersion} current=${node.version}) type=${msg?.type}`);
          return;
        }
        this.handleSignalingMessage(node, msg, myVersion);
      },
      (code?: number, reason?: string) => {
        if (node.version !== myVersion) {
          if (DEBUG) console.log(`[NodeConnMgr] STALE onclose (v=${myVersion} current=${node.version}`);
          return;
        }
        this.handleSocketClose(node, myVersion, code, reason);
      }
    );
    if (DEBUG) console.log(`[NodeConnMgr] connectNode: WS connected for node=${node.nodeId} v=${myVersion}`);

    // Check if superseded while awaiting
    if (node.version !== myVersion) {
      signaling.close();
      return;
    }
    node.turnCredentials = turnCredentials;
    node.socketState = 'open';

    // 2. WebRTC PC
    const webrtc = new WebRTCService(signaling);
    node.webrtc = webrtc;

    await webrtc.initialize(
      turnCredentials,
      // onTrack
      (event: RTCTrackEvent) => {
        if (node.version !== myVersion) return;
        const streamId = event.streams?.[0]?.id || '';
        const trackId = event.track.id || '';
        const camId = streamId.replace('camera-', '') || trackId.replace('video-', '');

        if (DEBUG) console.log(`[NodeConnMgr] Track: node=${node.nodeId} cam=${camId} v=${myVersion}`);

        const cam = node.cameras.get(camId);
        if (cam) {
          // RBAC denial is the one terminal state: the ULLFU will not send media
          // for it, so a track arriving for it is stale by definition.
          if (cam.state === 'access_denied') {
            if (DEBUG) console.log(`[NodeConnMgr] Ignoring track for access_denied cam=${camId}`);
            return;
          }

          // A track arriving for a camera we had given up on is the RECOVERY,
          // not a stale event — it is the ULLFU answering the resubscribe that the
          // player's countdown asked for, just later than our 20s watchdog. This
          // used to be dropped on the floor for any camera in camera_unreachable,
          // and since ontrack never fires twice for the same transceiver that
          // camera could then only be recovered by reloading the page.
          const mediaStream = event.streams?.[0] || new MediaStream([event.track]);
          cam.stream = mediaStream;
          cam.lastStream = mediaStream;
          cam.trackReceivedAt = Date.now();
          this.watchTrack(node, camId, event.track, myVersion);
          this.onStream(node.nodeId, camId, mediaStream);

          cam.state = 'rtc_established';
          cam.stallRetryCount = 0;
          cam.retryCount = 0;
          cam.recoveryAttempts = 0;
          cam.readoptCount = 0;
          // Cancel any pending timers
          const stallKey = `${node.nodeId}:${camId}`;
          const stallT = this.stallTimers.get(stallKey);
          if (stallT) { clearTimeout(stallT); this.stallTimers.delete(stallKey); }
          this.clearWaitingStreamTimeout(node.nodeId, camId);
          const st = this.signalingTimers.get(node.nodeId);
          if (st) { clearTimeout(st); this.signalingTimers.delete(node.nodeId); }
          this.onCameraState(node.nodeId, camId, 'rtc_established');
        }
      },
      // onConnectionStateChange
      (state: string) => {
        if (node.version !== myVersion) return;
        if (DEBUG) console.log(`[NodeConnMgr] RTC state node=${node.nodeId}: ${state} v=${myVersion}`);
        node.rtcState = state as NodeConnection['rtcState'];

        if (state === 'connected') {
          const dt = this.disconnectTimers.get(node.nodeId);
          if (dt) { clearTimeout(dt); this.disconnectTimers.delete(node.nodeId); }
          // Cameras still in signaling → now waiting for RTP packets
          // Skip cameras in reconnecting/camera_unreachable — stall resubscribe handles those
          for (const [, cam] of node.cameras) {
            if (cam.state === 'signaling') {
              cam.state = 'waiting_stream';
              this.onCameraState(node.nodeId, cam.cameraId, 'waiting_stream');
              this.startWaitingStreamTimeout(node, cam);
            }
          }
        } else if (state === 'disconnected') {
          const dt = this.disconnectTimers.get(node.nodeId);
          if (dt) clearTimeout(dt);
          const timer = setTimeout(() => {
            this.disconnectTimers.delete(node.nodeId);
            if (node.version !== myVersion || this.destroyed) return;
            if (node.rtcState === 'disconnected') {
              if (DEBUG) console.log(`[NodeConnMgr] RTC still disconnected after 15s — scheduling reconnect`);
              this.scheduleReconnect(node, 0);
            }
          }, 15000);
          this.disconnectTimers.set(node.nodeId, timer);
        } else if (state === 'failed') {
          const dt = this.disconnectTimers.get(node.nodeId);
          if (dt) { clearTimeout(dt); this.disconnectTimers.delete(node.nodeId); }
          // Mark cameras as network_error — ICE/TURN transport failed
          for (const [, cam] of node.cameras) {
            if (cam.state !== 'camera_unreachable') {
              cam.state = 'network_error';
              this.onCameraState(node.nodeId, cam.cameraId, 'network_error');
            }
          }
          this.scheduleReconnect(node, 0);
        }
      }
    );

    // Check if superseded while awaiting
    if (node.version !== myVersion) {
      webrtc.close();
      return;
    }

    // 3. Subscribe ALL cameras. May be empty when the connection is opened
    //    eagerly (kept warm) before any hexagon is clicked — the ULLFU is passive
    //    and only sends an offer once a camera is subscribed, so an empty
    //    connection just sits idle with the WS warm.
    const cameraIds = Array.from(node.cameras.keys());
    if (cameraIds.length > 0) {
      signaling.subscribe(cameraIds);
      if (DEBUG) console.log(`[NodeConnMgr] Subscribed ${cameraIds.length} cameras on node ${node.nodeId} v=${myVersion}`);

      for (const [, cam] of node.cameras) {
        cam.state = 'signaling';
        this.onCameraState(node.nodeId, cam.cameraId, 'signaling');
      }

      // 4. Signaling timeout — if no tracks in 25s, schedule reconnect
      this.armSignalingTimeout(node);
    }
  }

  /**
   * (Re)arm the per-node signaling timeout: if no camera reaches rtc_established
   * within SIGNALING_TIMEOUT_MS, schedule a reconnect. Self-skips when there are
   * no cameras (an eagerly-kept-warm connection legitimately has no tracks).
   */
  private armSignalingTimeout(node: NodeConnection): void {
    const prevSigTimer = this.signalingTimers.get(node.nodeId);
    if (prevSigTimer) clearTimeout(prevSigTimer);
    const myVersion = node.version;
    const sigTimer = setTimeout(() => {
      this.signalingTimers.delete(node.nodeId);
      if (node.version !== myVersion || this.destroyed || node.cameras.size === 0) return;
      let anyEstablished = false;
      for (const [, cam] of node.cameras) {
        if (cam.state === 'rtc_established') { anyEstablished = true; break; }
      }
      if (!anyEstablished) {
        console.warn(`[NodeConnMgr] Signaling timeout — no tracks for node ${node.nodeId} after ${SIGNALING_TIMEOUT_MS / 1000}s`);
        this.scheduleReconnect(node, 0);
      }
    }, SIGNALING_TIMEOUT_MS);
    this.signalingTimers.set(node.nodeId, sigTimer);
  }

  // ─── Message handlers ─────────────────────────────────────────

  private handleSignalingMessage(node: NodeConnection, msg: any, myVersion: number): void {
    if (node.version !== myVersion) return;

    // Packet-monitor traffic is routed here, ahead of the webrtc guard below,
    // because it is the only path every page shares. The signaling service
    // exposes a SINGLE onMessage callback, which this manager owns; a page that
    // wanted these messages had to own the socket instead. CamerasPage could
    // not, so it imported the dispatcher and never received anything to give it
    // — every monitor run there reported 0 packets at both ULLFU stages, and the
    // final report never arrived at all.
    //
    // It also silently corrupted the browser stage: the frontend stops counting
    // when the ULLFU's completion message arrives, falling back to a durationSec
    // +15s timer if it never does. With the message lost, every run measured the
    // browser over 75s against the ULLFU's 60s and duly reported the browser
    // receiving ~125% of what the ULLFU sent.
    //
    // Note this returns WITHOUT requiring node.webrtc: a monitor result must
    // still be delivered while the peer connection is being torn down and
    // rebuilt, which is exactly when a diagnostic run matters most.
    if (typeof msg?.type === 'string' && msg.type.startsWith('packet_monitor_')) {
      handlePacketMonitorMessage(msg);
      return;
    }

    const webrtc = node.webrtc;
    if (!webrtc) return;

    switch (msg.type) {
      case 'offer':
        if (!msg.sdp) {
          console.warn(`[NodeConnMgr] Offer with no SDP for node ${node.nodeId}`);
          return;
        }
        if (DEBUG) console.log(`[NodeConnMgr] Offer received for node ${node.nodeId} v=${myVersion} sdpLen=${msg.sdp.length}`);
        webrtc.handleOffer(msg.sdp).then(() => {
          if (DEBUG) console.log(`[NodeConnMgr] handleOffer SUCCESS for node ${node.nodeId} v=${myVersion} pcState=${webrtc.getConnectionState()}`);
        }).catch((err) => {
          console.error(`[NodeConnMgr] handleOffer FAILED for node ${node.nodeId} v=${myVersion}:`, err);
          if (node.version !== myVersion || this.destroyed) return;
          // A PeerConnection that cannot apply the ULLFU's offer will not apply the
          // next one either (mismatched m-lines after the ULLFU rebuilt its own PC,
          // corrupted signaling state, …). Swallowing this is what left cameras
          // resubscribing into a PC that could never answer — rebuild instead.
          this.escalateToNodeRebuild(node, 'offer could not be applied to the PeerConnection');
        });
        break;
      case 'ice-candidate':
        webrtc.handleIceCandidate({
          candidate: msg.candidate,
          sdpMid: msg.sdpMid,
          sdpMLineIndex: msg.sdpMLineIndex,
        });
        break;
      case 'camera-status':
        if (msg.cameraId && msg.status === 'denied') {
          // The ULLFU rejected this camera for the acting cloud user (no RBAC access).
          // TERMINAL: stop all retry/wait timers and never resubscribe — otherwise
          // the tile loops subscribe → no track → retry forever. The tile shows an
          // "Access denied" overlay instead.
          const cam = node.cameras.get(msg.cameraId);
          if (cam) {
            this.clearWaitingStreamTimeout(node.nodeId, msg.cameraId);
            const stallKey = `${node.nodeId}:${msg.cameraId}`;
            const stallT = this.stallTimers.get(stallKey);
            if (stallT) { clearTimeout(stallT); this.stallTimers.delete(stallKey); }
            cam.state = 'access_denied';
            cam.stream = null;
            cam.trackReceivedAt = 0;
            this.onStream(node.nodeId, msg.cameraId, null);
            this.onCameraState(node.nodeId, msg.cameraId, 'access_denied');
          }
        } else if (msg.cameraId && msg.status === 'offline') {
          const cam = node.cameras.get(msg.cameraId);
          if (cam) {
            cam.state = 'camera_unreachable';
            this.onCameraState(node.nodeId, msg.cameraId, 'camera_unreachable');
          }
        } else if (msg.cameraId && msg.status === 'online') {
          const cam = node.cameras.get(msg.cameraId);
          if (cam && cam.state !== 'access_denied' && !cam.stream) {
            // The ULLFU says this camera is fine on its side. If media is already
            // flowing on the track we hold, take it back; otherwise force a real
            // re-offer rather than waiting for a track the ULLFU has no reason to
            // send (it only offers when its subscription set CHANGES).
            if (!this.tryReadopt(node, cam)) {
              this.resubscribeFresh(node, [cam]);
            }
          }
        }
        break;
      case 'error':
        console.warn(`[NodeConnMgr] Error for ${node.nodeId}: ${msg.message || 'unknown'} cam=${msg.cameraId || 'all'}`);
        // A tunnel blip while we happened to send subscribe/answer/ice produces
        // this error. The tunnel only carries signaling — media keeps flowing —
        // so treat it exactly like tunnel_disconnected (grace + resume) instead
        // of tearing down the PC and killing every playing stream.
        if (msg.code === 'TUNNEL_UNAVAILABLE' || /tunnel not available/i.test(msg.message || '')) {
          this.handleTunnelDown(node);
          return;
        }
        if (msg.cameraId) {
          const cam = node.cameras.get(msg.cameraId);
          if (cam) {
            cam.state = 'camera_unreachable';
            this.onCameraState(node.nodeId, msg.cameraId, 'camera_unreachable');
          }
        } else {
          // Teardown first — invalidates stale signaling timeout and ensures
          // isNodeHealthy returns false so the scheduled reconnect actually fires
          this.bumpAndTeardown(node);
          for (const [, cam] of node.cameras) {
            cam.state = 'node_unreachable';
            this.onCameraState(node.nodeId, cam.cameraId, 'node_unreachable');
          }
          // Use tiered backoff based on retry count — prevents hammering
          // the server every 3s when tunnel is down for extended periods
          const retryNum = node.cameras.values().next().value?.retryCount ?? 0;
          const delay = this.getBackoffMs(retryNum);
          if (DEBUG) console.log(`[NodeConnMgr] All-cameras error — scheduling reconnect in ${delay}ms (retry ${retryNum})`);
          this.scheduleReconnect(node, delay);
        }
        break;
      case 'tunnel_disconnected':
        this.handleTunnelDown(node);
        break;
      case 'tunnel_reconnected': {
        // Relay is back. Cancel the pending full-reconnect and resume over the SAME WS+PC:
        // re-subscribe cameras that aren't already playing so the ULLFU re-offers them. Cameras
        // that kept streaming through the blip are untouched. If the local ULLFU lost the
        // session, the per-camera waiting_stream / signaling timeout still triggers a clean
        // reconnect as a safety net.
        const recoverT = this.tunnelRecoverTimers.get(node.nodeId);
        if (recoverT) { clearTimeout(recoverT); this.tunnelRecoverTimers.delete(node.nodeId); }
        if (!node.signaling || node.socketState !== 'open') {
          // Signaling WS isn't usable — fall back to a normal reconnect.
          this.scheduleReconnect(node, 0);
          break;
        }
        // Anything still receiving keeps its stream; only re-offer the rest. The
        // re-adopt check comes first because a camera can have kept flowing
        // through the blip with only our stream reference lost.
        const toResub = [...node.cameras.values()].filter((c) => {
          if (c.stream && c.state === 'rtc_established') return false;
          return !this.tryReadopt(node, c);
        });
        console.warn(`[NodeConnMgr] Tunnel relay back for ${node.nodeId} — resuming (${toResub.length} cameras to resubscribe)`);
        // resubscribeFresh, not a bare subscribe: the ULLFU ignores a subscribe for
        // a camera it still has in its subscription map, which is exactly the
        // state these cameras are in after a signaling-only blip.
        if (toResub.length > 0) this.resubscribeFresh(node, toResub);
        break;
      }
    }
  }

  /**
   * The tunnel relays only SIGNALING; WebRTC media flows browser↔TURN↔local ULLFU and
   * keeps playing through a brief tunnel blip. So DON'T tear down the PC (that kills
   * working previews). Keep already-playing cameras as-is; show others 'reconnecting'.
   * The cloud keeps the tunnel alive (connect_ws on active sessions) and sends
   * 'tunnel_reconnected' when it's back. Only if it stays down past the grace do we
   * fall back to a full signaling reconnect.
   */
  private handleTunnelDown(node: NodeConnection): void {
    console.warn(`[NodeConnMgr] Tunnel relay down for ${node.nodeId} — keeping media, grace ${TUNNEL_RECOVER_GRACE_MS}ms`);
    for (const [, cam] of node.cameras) {
      if (cam.stream && cam.state === 'rtc_established') continue; // keep playing
      cam.state = 'reconnecting';
      this.onCameraState(node.nodeId, cam.cameraId, 'reconnecting');
    }
    const prev = this.tunnelRecoverTimers.get(node.nodeId);
    if (prev) clearTimeout(prev);
    const myVersion = node.version;
    const t = setTimeout(() => {
      this.tunnelRecoverTimers.delete(node.nodeId);
      if (node.version !== myVersion || this.destroyed) return;
      // Tunnel never came back — do the full (destructive) reconnect to rebuild signaling.
      console.warn(`[NodeConnMgr] Tunnel still down after grace for ${node.nodeId} — full reconnect`);
      this.bumpAndTeardown(node);
      for (const [, cam] of node.cameras) {
        cam.state = 'node_unreachable';
        cam.retryCount = 0;
        cam.stallRetryCount = 0;
        this.onCameraState(node.nodeId, cam.cameraId, 'node_unreachable');
      }
      this.scheduleReconnect(node, 0);
    }, TUNNEL_RECOVER_GRACE_MS);
    this.tunnelRecoverTimers.set(node.nodeId, t);
  }

  private handleSocketClose(node: NodeConnection, myVersion: number, code?: number, reason?: string): void {
    if (node.version !== myVersion) return;
    if (DEBUG) console.log(`[NodeConnMgr] Socket closed node=${node.nodeId} v=${myVersion} rtc=${node.rtcState} code=${code} reason=${reason}`);
    // Capture BEFORE nulling — reconnectSignaling resumes this exact session so
    // the ULLFU-side peer connection (and flowing media) survives the WS blip.
    const prevSessionId = node.signaling?.getSessionId() || undefined;
    node.socketState = 'closed';
    node.signaling = null;

    if ((node.cameras.size === 0 && !node.keptAlive) || this.destroyed) return;

    // If doReconnect already scheduled a retry (e.g. from connectNode failure),
    // don't override it with a shorter timer — that causes rapid-fire retries
    if (this.reconnectTimers.has(node.nodeId)) {
      if (DEBUG) console.log(`[NodeConnMgr] Socket closed but reconnect already scheduled — skipping`);
      return;
    }

    // Tunnel unavailable (code 4002) — the node's tunnel to cloud is down.
    // Full teardown to invalidate stale timers and ensure isNodeHealthy=false.
    if (code === 4002) {
      if (DEBUG) console.log(`[NodeConnMgr] Tunnel unavailable (4002) for ${node.nodeId} — tearing down, retry in 10s`);
      this.bumpAndTeardown(node);
      for (const [, cam] of node.cameras) {
        cam.state = 'node_unreachable';
        this.onCameraState(node.nodeId, cam.cameraId, 'node_unreachable');
      }
      this.scheduleReconnect(node, 10000);
      return;
    }

    if (node.rtcState === 'connected') {
      // RTC still works — just reopen signaling, resuming the same session
      const v = node.version;
      setTimeout(() => {
        if (node.version !== v || this.destroyed || (node.cameras.size === 0 && !node.keptAlive)) return;
        this.reconnectSignaling(node, v, prevSessionId);
      }, 2000);
      return;
    }

    // Full reconnect needed
    for (const [, cam] of node.cameras) {
      cam.state = 'node_unreachable';
      this.onCameraState(node.nodeId, cam.cameraId, 'node_unreachable');
    }
    const retryNum = node.cameras.values().next().value?.retryCount ?? 0;
    this.scheduleReconnect(node, this.getBackoffMs(retryNum));
  }

  /**
   * Reopen ONLY the signaling WS while keeping the existing RTCPeerConnection
   * (media kept flowing browser↔TURN↔ULLFU through the WS blip).
   *
   * This is only sound when the cloud RESUMES the previous session: same
   * sessionId = the tunnel/ULLFU keep routing to the same ULLFU-side peer
   * connection, and re-subscribing already-playing cameras is a no-op there.
   * If the cloud could NOT resume (grace expired → fresh sessionId), the ULLFU
   * has a brand-new PC that can never pair with our old one — fall back to a
   * full reconnect instead of wedging both sides.
   */
  private async reconnectSignaling(node: NodeConnection, myVersion: number, resumeSessionId?: string): Promise<void> {
    if (node.socketState !== 'closed' || this.destroyed || node.version !== myVersion) return;
    try {
      node.socketState = 'connecting';
      const signaling = new SignalingService(node.nodeId);
      node.signaling = signaling;
      const { resumed } = await signaling.connect(
        (msg) => {
          if (node.version !== myVersion) return;
          this.handleSignalingMessage(node, msg, myVersion);
        },
        (code?: number, reason?: string) => {
          if (node.version !== myVersion) return;
          this.handleSocketClose(node, myVersion, code, reason);
        },
        resumeSessionId
      );
      if (node.version !== myVersion) { signaling.close(); return; }

      if (!resumed) {
        console.warn(`[NodeConnMgr] Session resume failed for ${node.nodeId} — full reconnect`);
        signaling.close();
        node.socketState = 'closed';
        node.signaling = null;
        this.scheduleReconnect(node, 0);
        return;
      }

      node.socketState = 'open';
      // Point the surviving PC's answer/ICE sender at the NEW socket — without
      // this every renegotiation reply goes to the dead WS and is dropped.
      node.webrtc?.setSignaling(signaling);

      // Playing cameras are left completely alone — their media never stopped.
      // Everything else needs a FORCED re-offer: the ULLFU skips a plain subscribe
      // for a camera already in its map, so the old bare subscribe here produced
      // nothing at all for precisely the cameras that needed something.
      const toResub = [...node.cameras.values()].filter((cam) => {
        if (cam.stream && cam.state === 'rtc_established') return false;
        return !this.tryReadopt(node, cam);
      });
      if (toResub.length > 0) {
        this.resubscribeFresh(node, toResub);
      }
      console.log(`[NodeConnMgr] Signaling resumed for node ${node.nodeId} (session kept, media preserved)`);
    } catch (err) {
      if (node.version !== myVersion) return;
      node.socketState = 'closed';
      node.signaling = null;
      console.warn(`[NodeConnMgr] Signaling reconnect failed for ${node.nodeId}:`, err);
      this.scheduleReconnect(node, 5000);
    }
  }
}
